import { useMemo } from "react";
import type { ReportFilters, ReportOptions } from "@/components/feature/reports/types";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import { agenceLocataireNomComplet, AGENCE_BIEN_TYPE_LABELS } from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { buildAgenceReport, getAgenceStatutConfig } from "./buildReport";
import { AGENCE_REPORT_TYPES } from "./catalog";

/**
 * Assemble le moteur de rapports de l'espace Agence à partir des stores de
 * l'espace. Le rapport ne s'appuie que sur les données accessibles au compte
 * connecté. Le jour où la base de données est branchée, seuls les stores
 * changent : ce hook et le moteur de rapports restent inchangés.
 */
export function useAgenceReports() {
  const { proprietaires } = useProprietaires();
  const { immeubles, biens, locataires, contrats, loyers, paiements, depenses, interventions, documents } =
    useAgenceData();

  const options: ReportOptions = useMemo(() => {
    const proprietaireByLocataire = new Map<string, Set<string>>();
    contrats.forEach((contrat) => {
      const set = proprietaireByLocataire.get(contrat.locataireId) ?? new Set<string>();
      set.add(contrat.proprietaireId);
      proprietaireByLocataire.set(contrat.locataireId, set);
    });

    return {
      immeubles: immeubles.map((immeuble) => ({
        value: immeuble.id,
        label: immeuble.nom,
        proprietaireId: immeuble.proprietaireId,
      })),
      biens: biens.map((bien) => ({
        value: bien.id,
        label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}`,
        immeubleId: bien.immeubleId,
        proprietaireId: bien.proprietaireId,
      })),
      locataires: locataires.map((locataire) => {
        const owners = proprietaireByLocataire.get(locataire.id);
        return {
          value: locataire.id,
          label: agenceLocataireNomComplet(locataire),
          proprietaireId: owners && owners.size === 1 ? Array.from(owners)[0] : undefined,
        };
      }),
      proprietaires: proprietaires.map((proprietaire) => ({
        value: proprietaire.id,
        label: proprietaireNomComplet(proprietaire),
      })),
    };
  }, [immeubles, biens, locataires, contrats, proprietaires]);

  const buildReport = (typeId: string, filters: ReportFilters) =>
    buildAgenceReport(typeId, filters, {
      proprietaires,
      immeubles,
      biens,
      locataires,
      contrats,
      loyers,
      paiements,
      depenses,
      interventions,
      documents,
    });

  return {
    reportTypes: AGENCE_REPORT_TYPES,
    options,
    getStatutConfig: getAgenceStatutConfig,
    buildReport,
  };
}